import {useSyncExternalStore} from 'react'

type ScriptCrossOrigin = 'anonymous' | 'use-credentials'

type ServerRenderedScripts = {
  /** `src` of every `<script>` already in the document head from the initial hard load (Rails). */
  readonly present: ReadonlySet<string>
  /** CORS mode Rails used for its asset scripts, mirrored onto anything we render. */
  readonly crossOrigin: ScriptCrossOrigin
}

// A one-time read of the scripts the server (Rails) already delivered. Cached so the
// useSyncExternalStore snapshot is referentially stable: the server-rendered set never changes
// after the initial paint, so the store never has to notify subscribers.
let cached: ServerRenderedScripts | null = null

function readServerRenderedScripts(): ServerRenderedScripts {
  // Rails renders its bundle tags (and React hoists ours) into <head>, so that's the only scope
  // that can collide — and scoping there avoids matching unrelated body scripts.
  const present = new Set<string>()
  for (const el of document.head.querySelectorAll('script[src]')) {
    const src = el.getAttribute('src')
    if (src) present.add(src)
  }

  const withCors = document.head.querySelector<HTMLScriptElement>('script[src][crossorigin]')
  const crossOrigin: ScriptCrossOrigin = withCors?.crossOrigin === 'use-credentials' ? 'use-credentials' : 'anonymous'

  return {present, crossOrigin}
}

const store = {
  // The snapshot is a one-time read of the server-rendered DOM and never changes, so there is
  // nothing to subscribe to — a no-op unsubscribe satisfies the store contract.
  subscribe(): () => void {
    return () => {}
  },
  getSnapshot(): ServerRenderedScripts {
    if (cached === null) cached = readServerRenderedScripts()
    return cached
  },
  // No DOM on the server: return null so callers render nothing (scripts *and* styles) and let Rails
  // own the hard-load assets. Emitting them during SSR would double-execute bundles and ship a second
  // copy of each stylesheet.
  getServerSnapshot(): ServerRenderedScripts | null {
    return null
  },
}

const NONE: readonly string[] = []

/**
 * Returns the global assets the active route needs that should be rendered *now* — i.e. client-only,
 * after hydration: `scriptSrcs` (requested bundle `src`s minus any already on the page), `styleHrefs`
 * (requested stylesheet `href`s, unfiltered), and the `crossOrigin` to render them with.
 *
 * Everything is suppressed for the server snapshot (and the hydration render that mirrors it). Rails
 * already emits the current route's bundles (`<script type="module" defer>`) and stylesheets
 * (`<link rel="stylesheet">`), so emitting them during `renderToString` (Alloy SSR) would ship a
 * second copy of each: double-executing scripts, and duplicating stylesheets into the app fragment
 * *after* Rails' head, which can also shift cascade order. Rails owns the hard-load paint; we only add
 * what a soft navigation introduces.
 *
 * On the client after hydration the two asset kinds differ:
 * - Scripts need a `src` filter — React only dedupes an async script against an existing
 *   `script[async][src]`, never Rails' `<script defer>`, so we drop bundles already on the page. What
 *   remains is hoisted and kept as a resource across route changes (a back-nav re-acquires the tag).
 * - Stylesheets need no filter — React dedupes by `href` and adopts Rails' existing `<link>`, so the
 *   landing route's stylesheets adopt Rails' tags and a soft navigation's are created fresh.
 */
export function useGlobalAssets(
  js: string[],
  css: string[],
): {scriptSrcs: readonly string[]; styleHrefs: readonly string[]; crossOrigin: ScriptCrossOrigin} {
  const serverRendered = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getServerSnapshot)

  if (!serverRendered) return {scriptSrcs: NONE, styleHrefs: NONE, crossOrigin: 'anonymous'}

  return {
    scriptSrcs: js.filter(src => !serverRendered.present.has(src)),
    styleHrefs: css,
    crossOrigin: serverRendered.crossOrigin,
  }
}

/** Test-only: drops the cached read of the server-rendered DOM so each test seeds fresh. */
export function resetGlobalScriptsStoreForTests(): void {
  cached = null
}
